// DispatchModal — Preview & Copy modal for TenantBridge → MaintainX handoff.
// Sprint 4 email-less MVP. Mario clicks "Send to MaintainX" → this modal
// opens with the fully formatted payload (unit, priority, entry auth,
// full conversation) rendered in a monospace pre. Big Copy button + PDF
// download. Mario dispatches manually, then pastes the MaintainX WO# back
// to close the loop.
import { useEffect, useState } from "react";
import { XCircle, Copy, DownloadSimple, CheckCircle, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function DispatchModal({ threadId, unit, onClose, onDispatched }) {
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/tenant-bridge/threads/${threadId}/dispatch-preview`)
      .then(r => setPreview(r.data.payload || ""))
      .catch(() => toast.error("Couldn't load preview"))
      .finally(() => setLoading(false));
  }, [threadId]);

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      toast.success("Payload copied — paste into MaintainX");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Copy failed — select the text manually"); }
  };

  const pdfHref = `${BASE}/api/tenant-bridge/threads/${threadId}/dispatch.pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`;

  const markDispatched = async () => {
    setBusy(true);
    try {
      await api.post(`/tenant-bridge/threads/${threadId}/route`, {
        target: "maintainx", ref: ref.trim(),
      });
      toast.success("Thread closed · MaintainX dispatch logged");
      onDispatched();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't close")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="dispatch-modal">
      <div className="w-full max-w-2xl bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* HEADER */}
        <div className="p-3 border-b-2 border-slate-200 flex items-center justify-between bg-cyan-50">
          <div className="flex items-center gap-2">
            <Envelope weight="fill" size={20} className="text-cyan-600"/>
            <div>
              <div className="font-black uppercase tracking-widest text-sm">Send to MaintainX</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Preview & copy — unit {unit || "—"}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-black"><XCircle size={22}/></button>
        </div>

        {/* PAYLOAD PREVIEW */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="p-3 bg-slate-50 border-l-4 border-cyan-500 text-xs text-slate-700 mb-3">
            <b>Manual dispatch mode:</b> Field Radar generates the WO payload — you paste it into MaintainX (or attach the PDF). Auto-send-to-MaintainX will land in a future toggle.
          </div>
          {loading ? (
            <div className="text-center py-8 text-slate-400 font-black uppercase text-[10px] tracking-widest">Loading preview…</div>
          ) : (
            <pre data-testid="dispatch-preview-text"
              className="whitespace-pre-wrap p-3 bg-[#0f172a] text-amber-100 text-[11px] font-mono border-2 border-[#0f172a] max-h-[45vh] overflow-y-auto">
              {preview}
            </pre>
          )}
        </div>

        {/* ACTIONS */}
        <div className="p-3 border-t-2 border-slate-200 bg-slate-50 space-y-2">
          <div className="flex gap-2">
            <button onClick={copyPayload} disabled={loading} data-testid="dispatch-copy-btn"
              className={`flex-1 h-11 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 ${
                copied ? "bg-emerald-500 text-white" : "bg-cyan-500 hover:bg-cyan-600 text-white"
              } disabled:opacity-40`}>
              {copied ? <><CheckCircle weight="fill" size={14}/> Copied!</> : <><Copy weight="fill" size={14}/> Copy payload</>}
            </button>
            <a href={pdfHref} target="_blank" rel="noreferrer" data-testid="dispatch-pdf-btn"
              className={`h-11 px-4 bg-white border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest hover:bg-slate-100 inline-flex items-center gap-2 ${loading ? "opacity-40 pointer-events-none" : ""}`}>
              <DownloadSimple weight="bold" size={14}/> PDF
            </a>
          </div>
          <div className="pt-2 border-t border-slate-200">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">MaintainX WO # (optional)</label>
            <div className="flex gap-2 mt-1">
              <input value={ref} onChange={e => setRef(e.target.value)}
                data-testid="dispatch-ref-input"
                placeholder="e.g. WO-8421"
                className="flex-1 h-10 px-3 border-2 border-slate-300 text-sm font-mono"/>
              <button onClick={markDispatched} disabled={busy} data-testid="dispatch-close-btn"
                className="h-10 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center gap-1">
                <CheckCircle weight="fill" size={12}/> {busy ? "…" : "Mark dispatched"}
              </button>
            </div>
            <div className="text-[10px] text-slate-400 italic mt-1">
              Paste the MaintainX WO# here so Field Radar can link the two systems.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
